import { coerceNumberProperty } from '@angular/cdk/coercion';
import { Inject, Injectable, Optional } from '@angular/core';
import { MAT_DATE_FORMATS, MAT_DATE_LOCALE, MatDateFormats } from '@angular/material/core';
import { MomentDateAdapter } from '@angular/material-moment-adapter';
import { NK_PARSER_DICTIONARY } from '@nitro/core/common';
import * as _moment from 'moment';
// eslint-disable-next-line no-duplicate-imports
import { Moment } from 'moment';

import { NK_DATE_TIME_PARSERS } from '../parsers/date-time.parsers';

import { DateTimeAdapter } from './date-time-adapter';

const moment = _moment;

@Injectable()
export class NkMomentDateTimeAdapter extends MomentDateAdapter implements DateTimeAdapter<Moment> {
  constructor(
    @Optional() @Inject(MAT_DATE_FORMATS) private dateFormats: MatDateFormats,
    @Optional() @Inject(MAT_DATE_LOCALE) dateLocale: string,
    @Optional()
    @Inject(NK_DATE_TIME_PARSERS)
    private dict?: NK_PARSER_DICTIONARY<Moment>
  ) {
    super(dateLocale);
    // this.dict = this.dict || NK_DEFAULT_DATE_TIME_PARSERS;
  }

  format = (date: Moment, displayFormat: string) => {
    if (this.dateFormats) {
      return super.format(date, displayFormat || (this.dateFormats ? this.dateFormats.display.dateInput : ''));
    } else {
      return date.toString();
    }
  };

  parse = (value: any, parseFormat: string | string[]): Moment | null => {
    if (value && typeof value === 'string' && this.dict) {
      for (const xp of Array.from(this.dict.values())) {
        const mxp = xp(value.trim(), this.locale);
        if (mxp && mxp.isValid()) {
          return mxp;
        }
      }
    }

    const re = moment(value, parseFormat);

    if (re.isValid()) {
      return re;
    } else {
      return null;
    }
  };

  sameDateTime(first: Moment, second: Moment) {
    if (first && second) {
      const firstValid = this.isValid(first);
      const secondValid = this.isValid(second);
      if (firstValid && secondValid) {
        return !this.compareDateTime(first, second);
      }
      return firstValid === secondValid;
    }
    return !first === !second;
  }

  getHours(date: Moment): number {
    return moment(date).hours();
  }

  getMinutes(date: Moment): number {
    return moment(date).minutes();
  }

  getSeconds(date: Moment): number {
    return moment(date).seconds();
  }

  getMilliseconds(date: Moment): number {
    return moment(date).milliseconds();
  }

  getZeroUnix(date: Moment): number {
    return this.setTimeFromDate(moment(0), date).valueOf();
  }

  setTime(date: Moment, hh: number | string, mm?: number | string, ss?: number | string, ms?: number | string) {
    if (!date) {
      return;
    }
    const timeInput: _moment.MomentInputObject = {};
    timeInput.hours = coerceNumberProperty(hh);
    if (mm !== void 0) {
      timeInput.minutes = coerceNumberProperty(mm);
    }
    if (ss !== void 0) {
      timeInput.seconds = coerceNumberProperty(ss);
    }
    if (ms !== void 0) {
      timeInput.milliseconds = coerceNumberProperty(ms);
    }
    return date ? moment(date).set(timeInput).clone() : date;
  }

  setTimeFromDate(date: Moment, dateTime: Moment) {
    const { hours, minutes, seconds, milliseconds } = moment(dateTime).toObject();

    return moment(date)
      .set({
        hours: hours,
        minutes: minutes,
        seconds: seconds,
        milliseconds: milliseconds
      })
      .clone();
  }

  setMeridian(date: Moment, pm: boolean) {
    const h = this.getHours(date);
    const newh = pm ? h + 12 : h - 12;
    return this.setTime(date, newh);
  }

  clampDate(date: Moment, min?: Moment | null, max?: Moment | null): Moment {
    if (min && this.compareDateTime(date, min) < 0) {
      return min;
    }
    if (max && this.compareDateTime(date, max) > 0) {
      return max;
    }
    return date;
  }

  compareDateTime(date1: Moment, date2: Moment): number {
    if (!date1 && !date2) {
      return 0;
    } else if (!date1) {
      return -1;
    } else if (!date2) {
      return 1;
    } else {
      return date1.valueOf() - date2.valueOf();
    }
  }

  validOrNull(obj: any): Moment | null {
    return this.isDateInstance(obj) && this.isValid(obj) ? obj : null;
  }
}
